import { useState, useMemo } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useMinsFor } from "@/lib/useBaseMins";
import { usePayrollSettings } from "@/context/PayrollSettingsContext";
import { lineBreakdown } from "@/lib/payrollCompute";
import { usePayrollResolver } from "@/lib/usePayrollResolver";
import { useReportLogo, addPdfLogo } from "@/lib/useLogo";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { PeriodSelector } from "@/components/PeriodSelector";
import { brl, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileText, TrendingUp, Gauge } from "lucide-react";
import { jsPDF } from "jspdf";

const CATS = [
  { k: "all", label: "Todos" },
  { k: "ciclista", label: "Ciclista" },
  { k: "motoboy", label: "Motoboy" },
  { k: "administrativo", label: "Administrativo" },
];
const CAT_LABEL = { ciclista: "Ciclista", motoboy: "Motoboy", administrativo: "Administrativo" };

const catOf = (l) => {
  const v = String(l.vehicle_type || l.employment_type || "").toLowerCase();
  if (v === "ciclista") return "ciclista";
  if (v === "motociclista" || v === "motoboy") return "motoboy";
  return "administrativo";
};

export default function CustoOperacional() {
  const logo = useReportLogo();
  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const today = now.toISOString().slice(0, 10);
  const [start, setStart] = useState(firstDay);
  const [end, setEnd] = useState(today);
  const [cat, setCat] = useState("all");

  const month = (start || today).slice(0, 7);
  const yr = Number((start || today).slice(0, 4));
  const mo = Number((start || today).slice(5, 7));
  // Quinzena vem do seletor de Período: 1–15 = 1ª, 16–fim = 2ª, senão Mensal.
  const sDay = Number((start || "").slice(8, 10)) || 1;
  const eDay = Number((end || "").slice(8, 10)) || 31;
  const period = sDay >= 16 ? 2 : eDay <= 15 ? 1 : "mensal";

  const R = usePayrollResolver(month);
  const payrollRaw = useFetch("/admin/payroll", period === "mensal" ? { month } : { month, quinzena: period });
  const payQ1Raw = useFetch("/admin/payroll", { month, quinzena: 1 });
  const payQ2Raw = useFetch("/admin/payroll", { month, quinzena: 2 });
  const payroll = payrollRaw;
  const reports1 = useFetch("/admin/reports", { year: yr, month: mo, quinzena: 1 });
  const reports2 = useFetch("/admin/reports", { year: yr, month: mo, quinzena: 2 });
  const cfg = useFetch("/config");
  const tiersData = useFetch("/admin/bonus-tiers");
  const { settings } = usePayrollSettings();

  const mins = useMemo(() => ({ ...(cfg.data || {}), mestre_min: cfg.data?.mestre_min ?? settings.mestre_min }), [cfg.data, settings.mestre_min]);
  const minsFor = useMinsFor();
  const tiers = useMemo(() => tiersData.data?.tiers || [], [tiersData.data]);
  const deliveriesByUser = useMemo(() => {
    // O endpoint /admin/reports é por quinzena: somamos as quinzenas que o período cobre.
    const sDay = Number((start || "").slice(8, 10)) || 1;
    const eDay = Number((end || "").slice(8, 10)) || 31;
    const useQ1 = !start || sDay <= 15;
    const useQ2 = !end || eDay >= 16;
    const m = {};
    if (useQ1) (reports1.data?.rows || []).forEach((r) => { m[r.id] = (m[r.id] || 0) + (r.deliveries || 0); });
    if (useQ2) (reports2.data?.rows || []).forEach((r) => { m[r.id] = (m[r.id] || 0) + (r.deliveries || 0); });
    return m;
  }, [reports1.data, reports2.data, start, end]);

  const allRows = useMemo(() => {
    const qDeliv = (rep) => { const m = {}; (rep.data?.rows || []).forEach((r) => { m[r.id] = r.deliveries || 0; }); return m; };
    const opts = (uid, q, deliveries) => ({ ...R.flags, mins: minsFor(uid, mins), tiers, deliveries, individual: R.individualFor(uid, q), period: q, contract_type: R.contractOf(uid) });
    const q1Lines = R.prepare(payQ1Raw.data)?.payroll?.lines || [];
    const q2Lines = R.prepare(payQ2Raw.data)?.payroll?.lines || [];
    // Mensal = 1ª + 2ª quinzena, cada uma respeitando o campo quinzena do componente.
    if (period === "mensal" && (q1Lines.length || q2Lines.length)) {
      const d1 = qDeliv(reports1), d2 = qDeliv(reports2);
      const map = {};
      [[q1Lines, 1, d1], [q2Lines, 2, d2]].forEach(([lines, q, dm]) => lines.forEach((l) => {
        const b = lineBreakdown(R.resolveSalary(l), opts(l.user_id, q, dm[l.user_id] || 0));
        const e = map[l.user_id] || (map[l.user_id] = { id: l.user_id, name: l.name, cat: catOf(l), prov: 0, desc: 0, net: 0, deliveries: deliveriesByUser[l.user_id] || 0 });
        e.prov += b.prov; e.desc += b.desc; e.net += b.net;
      }));
      return Object.values(map);
    }
    const qz = R.flags.quinzenaItems;
    return (R.prepare(payrollRaw.data)?.payroll?.lines || []).map((l) => {
      const deliveries = deliveriesByUser[l.user_id] || 0;
      // Sem as folhas das quinzenas: no mensal o componente de uma quinzena só conta metade do valor mensal.
      const rl = R.resolveSalary(l, period === "mensal" ? 2 : 1);
      const line = period === "mensal" ? { ...rl, items: (rl.items || []).map((it) => (qz[it.id] ? { ...it, value: (Number(it.value) || 0) / 2 } : it)) } : rl;
      const b = lineBreakdown(line, opts(l.user_id, period, deliveries));
      return { id: l.user_id, name: l.name, cat: catOf(l), prov: b.prov, desc: b.desc, net: b.net, deliveries };
    });
  }, [payrollRaw.data, payQ1Raw.data, payQ2Raw.data, reports1, reports2, deliveriesByUser, mins, tiers, minsFor, period, R]);

  const rows = allRows.filter((r) => (cat === "all" ? true : r.cat === cat));
  const totals = rows.reduce((a, r) => ({ prov: a.prov + r.prov, desc: a.desc + r.desc, net: a.net + r.net }), { prov: 0, desc: 0, net: 0 });

  const efficiency = useMemo(() => {
    const drv = rows.filter((r) => r.cat === "ciclista" || r.cat === "motoboy")
      .map((r) => ({ ...r, cpe: r.deliveries > 0 ? r.net / r.deliveries : null }));
    const withD = drv.filter((r) => r.cpe != null).sort((a, b) => a.cpe - b.cpe);
    const noD = drv.filter((r) => r.cpe == null);
    return [...withD, ...noD];
  }, [rows]);

  if (payroll.loading && !payroll.data) return <Loading />;
  if (payroll.error) return <ErrorState error={payroll.error} onRetry={payroll.refetch} />;

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    addPdfLogo(doc, logo, { top: 10 });
    const M = 40, W = doc.internal.pageSize.getWidth();
    let y = M;
    doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text("Custo Operacional", M, y); y += 18;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
    doc.text(`Periodo: ${start || "-"} a ${end || "-"}    Categoria: ${CATS.find((c) => c.k === cat)?.label}`, M, y); y += 20;
    doc.setTextColor(20); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    const cols = [M, M + 200, M + 300, M + 400]; // nome, prov, desc, liquido
    doc.text("Colaborador", cols[0], y); doc.text("Proventos", cols[1], y); doc.text("Descontos", cols[2], y); doc.text("Liquido", cols[3], y);
    y += 6; doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 12;
    doc.setFont("helvetica", "normal");
    rows.forEach((r) => {
      if (y > doc.internal.pageSize.getHeight() - 60) { doc.addPage(); y = M; }
      doc.text(String(r.name || "").slice(0, 34), cols[0], y);
      doc.text(brl(r.prov), cols[1], y); doc.text(brl(r.desc), cols[2], y); doc.text(brl(r.net), cols[3], y);
      y += 14;
    });
    y += 4; doc.setDrawColor(150); doc.line(M, y, W - M, y); y += 14;
    doc.setFont("helvetica", "bold");
    doc.text(`TOTAL GERAL (${rows.length})`, cols[0], y);
    doc.text(brl(totals.prov), cols[1], y); doc.text(brl(totals.desc), cols[2], y); doc.text(brl(totals.net), cols[3], y);
    y += 24;
    if (efficiency.length) {
      if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = M; }
      doc.setFontSize(12); doc.text("Eficiencia por Entregador", M, y); y += 16;
      doc.setFontSize(9);
      doc.text("Entregador", cols[0], y); doc.text("Custo", cols[1], y); doc.text("Entregas", cols[2], y); doc.text("Custo/entrega", cols[3], y);
      y += 6; doc.line(M, y, W - M, y); y += 12; doc.setFont("helvetica", "normal");
      efficiency.forEach((r) => {
        if (y > doc.internal.pageSize.getHeight() - 50) { doc.addPage(); y = M; }
        doc.text(String(r.name || "").slice(0, 34), cols[0], y);
        doc.text(brl(r.net), cols[1], y); doc.text(num(r.deliveries), cols[2], y);
        doc.text(r.cpe != null ? brl(r.cpe) : "-", cols[3], y);
        y += 14;
      });
    }
    doc.save(`custo_operacional_${start}_${end}.pdf`);
  };

  return (
    <div className="space-y-5" data-testid="custo-operacional">
      <PeriodSelector start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="custo-period" />

      <div className="flex flex-wrap items-center gap-2">
        {CATS.map((c) => (
          <button key={c.k} onClick={() => setCat(c.k)} data-testid={`custo-cat-${c.k}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${cat === c.k ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
            {c.label}
          </button>
        ))}
        <span className="ml-auto text-sm text-slate-500">{rows.length} colaborador(es)</span>
        <Button variant="outline" className="border-slate-700 text-slate-300" onClick={downloadPdf} data-testid="custo-pdf">
          <FileText className="h-4 w-4" /> Baixar PDF
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
        <table className="w-full text-sm" data-testid="custo-table">
          <thead>
            <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Colaborador</th>
              <th className="px-4 py-3 font-semibold">Tipo</th>
              <th className="px-4 py-3 text-right font-semibold">Proventos</th>
              <th className="px-4 py-3 text-right font-semibold">Descontos</th>
              <th className="px-4 py-3 text-right font-semibold">Custo líquido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 && <tr><td colSpan={5}><Empty label="Nenhum colaborador no filtro" /></td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`custo-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-slate-100">{r.name}</td>
                <td className="px-4 py-3 text-slate-400">{CAT_LABEL[r.cat]}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-300">{brl(r.prov)}</td>
                <td className="px-4 py-3 text-right font-mono text-rose-300">{brl(r.desc)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-slate-100">{brl(r.net)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-700 bg-slate-950/50 font-semibold">
                <td className="px-4 py-3 text-slate-200" colSpan={2}>Total geral da empresa</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-300" data-testid="custo-total-prov">{brl(totals.prov)}</td>
                <td className="px-4 py-3 text-right font-mono text-rose-300" data-testid="custo-total-desc">{brl(totals.desc)}</td>
                <td className="px-4 py-3 text-right font-mono text-white" data-testid="custo-total-net">{brl(totals.net)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="custo-eficiencia">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-orange-400" />
          <h3 className="text-base font-medium text-slate-200">Eficiência por Entregador</h3>
          <span className="text-xs text-slate-500">(custo por entrega no período — menor é melhor; entregas apuradas por quinzena)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="eficiencia-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Entregador</th>
                <th className="px-4 py-3 text-right font-semibold">Custo total</th>
                <th className="px-4 py-3 text-right font-semibold">Entregas</th>
                <th className="px-4 py-3 text-right font-semibold">Custo / entrega</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {efficiency.length === 0 && <tr><td colSpan={5}><Empty label="Nenhum entregador no filtro" /></td></tr>}
              {efficiency.map((r, i) => (
                <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`eficiencia-row-${r.id}`}>
                  <td className="px-4 py-3 text-slate-500">{r.cpe != null ? i + 1 : "—"}</td>
                  <td className="px-4 py-3 font-medium text-slate-100">
                    {r.name}
                    {r.cpe != null && i === 0 && <span className="ml-2 inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300"><Gauge className="h-3 w-3" /> mais eficiente</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-200">{brl(r.net)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{num(r.deliveries)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-100" data-testid={`eficiencia-cpe-${r.id}`}>{r.cpe != null ? brl(r.cpe) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
